"use client";

import { Award, BadgeCheck, GraduationCap, ShieldCheck, Medal, type LucideIcon } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import { Container } from "@/components/ui/Container";
import type { SiteCertification } from "@/lib/certifications";

const icons: LucideIcon[] = [BadgeCheck, GraduationCap, Award, ShieldCheck, Medal];

function Seal({ i, image }: { i: number; image?: string }) {
  const Icon = icons[i % icons.length];
  if (image) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img src={image} alt="" loading="lazy" className="h-14 w-14 shrink-0 rounded-full border border-gold-500/40 bg-white object-cover" />
    );
  }
  return (
    <span className="relative inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-gold-500/40 bg-gold-500/10 text-gold-700">
      <Icon className="h-7 w-7" strokeWidth={1.6} />
      <span className="absolute inset-1.5 rounded-full border border-dashed border-gold-500/35" aria-hidden />
    </span>
  );
}

function CertCard({ cert, i }: { cert: SiteCertification; i: number }) {
  return (
    <div className="flex h-full items-start gap-4 rounded-2xl border border-line bg-cream px-6 py-5 shadow-soft">
      <Seal i={i} image={cert.image} />
      <span className="leading-tight">
        {cert.issuer && (
          <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-brand-700">{cert.issuer}</span>
        )}
        <span className="mt-1 block font-display text-base font-semibold text-ink sm:text-lg">{cert.title}</span>
        {cert.note && <span className="mt-1 block text-xs text-ink-muted">{cert.note}</span>}
      </span>
    </div>
  );
}

/** Looping certification carousel that glides continuously (delay 0 + linear easing); pauses on hover. */
export function CertificationSwiper({ certifications }: { certifications: SiteCertification[] }) {
  // Fewer than 3 certifications: show them statically, no looping.
  if (certifications.length < 3) {
    return (
      <Container>
        <div className="flex flex-wrap gap-4">
          {certifications.map((c, i) => (
            <div key={i} className="w-[19rem] sm:w-[22rem]">
              <CertCard cert={c} i={i} />
            </div>
          ))}
        </div>
      </Container>
    );
  }
  // Swiper's loop needs more slides than it shows at once, so repeat short lists.
  const slides = certifications.length < 8 ? [...certifications, ...certifications, ...certifications] : certifications;
  return (
    <Swiper
      modules={[Autoplay]}
      loop
      spaceBetween={16}
      slidesPerView="auto"
      speed={8000}
      allowTouchMove={false}
      autoplay={{ delay: 0, disableOnInteraction: false, pauseOnMouseEnter: true }}
      className="pb-2! [&_.swiper-wrapper]:ease-linear!"
    >
      {slides.map((c, i) => (
        <SwiperSlide key={i} className="h-auto! w-[19rem]! sm:w-[22rem]!">
          <CertCard cert={c} i={i} />
        </SwiperSlide>
      ))}
    </Swiper>
  );
}
